import React, {
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useContext,
} from "react";

import { PermissionsContext } from "../../../utils/PermissionsContext";
import api from "../../../utils/api";
import DOMPurify from 'dompurify';

import {
  Box,
  Card,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Link,
  CircularProgress,
  Chip,
} from "@mui/material";

import {
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  PictureAsPdf as PdfIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Category as CategoryIcon,
  PendingActions as PendingActionsIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import ArchitecturalLetterTemplate from "../ArchitecturalLetterTemplate";
import RequestFilesViewer from "./RequestFilesViewer";
import EditArchitecturalRequest from "../EditArchitecturalRequests";

// ── Batch panel — shown when request is part of a multi-category batch ────────
const STATUS_COLORS = {
  Pending:  "warning", Approved: "success", Denied: "error",
  "In Review": "info", Completed: "success", Cancelled: "default",
};

function BatchPanel({ batchId, currentId, siblings, onBatchStatusChange }) {
  const [applying, setApplying] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  const applyToAll = async (status) => {
    setApplying(true);
    try {
      await api.put(`/arc-request-batches/${batchId}/status`, { status });
      setSnack({ open: true, msg: `All requests in this batch set to "${status}"`, sev: "success" });
      onBatchStatusChange?.();
    } catch {
      setSnack({ open: true, msg: "Failed to update batch status", sev: "error" });
    } finally { setApplying(false); }
  };

  return (
    <Card sx={{ borderRadius: "14px", border: "2px solid rgba(47,99,173,0.18)", boxShadow: "none", p: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CategoryIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142" }}>
            Batch Request — {siblings.length + 1} categories submitted together
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" variant="contained" color="success" disabled={applying}
            onClick={() => applyToAll("Approved")}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 12 }}>
            Approve All
          </Button>
          <Button size="small" variant="contained" color="error" disabled={applying}
            onClick={() => applyToAll("Denied")}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 12 }}>
            Deny All
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {/* Current request */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: "8px", bgcolor: "rgba(47,99,173,0.06)", border: "1px solid rgba(47,99,173,0.15)" }}>
          <Chip label="This" size="small" color="primary" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
          <Typography variant="body2" sx={{ flex: 1, fontSize: 13, color: "#223142", fontWeight: 600 }}>
            Current request
          </Typography>
        </Box>
        {/* Siblings */}
        {siblings.map(s => (
          <Box key={s.id} component="a" href={`/home/architecturalrequest/${s.id}`}
            sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: "8px",
              bgcolor: "rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.07)",
              textDecoration: "none", cursor: "pointer",
              "&:hover": { bgcolor: "rgba(47,99,173,0.04)" } }}>
            <Typography variant="body2" sx={{ flex: 1, fontSize: 13, color: "#2f63ad" }}>
              #{s.id} — {s.arc_category_breadcrumb || `Category request`}
            </Typography>
            <Chip label={s.status} size="small"
              color={STATUS_COLORS[s.status] || "default"}
              sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
          </Box>
        ))}
      </Box>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.sev} sx={{ width: "100%" }}>{snack.msg}</Alert>
      </Snackbar>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const RequestDetailsPanel = forwardRef(
  ({ requestDetails, onUpdate, currentUserId, member }, ref) => {
    const [types, setTypes] = useState([]);
    const [subtypes, setSubtypes] = useState([]);
    const [multiTypes, setMultiTypes] = useState([]);
    const [editMultiOpen, setEditMultiOpen] = useState(false);
    const [editMultiEntries, setEditMultiEntries] = useState([]);
    const [editMultiSubtypes, setEditMultiSubtypes] = useState({}); // { idx: [subtypes] }
    const [savingMulti, setSavingMulti] = useState(false);
    const [priorities, setPriorities] = useState([]);
    const [users, setUsers] = useState([]);
    const [letterLogs, setLetterLogs] = useState([]);
    const [selectedUser, setSelectedUser] = useState("");
    const [selectedType, setSelectedType] = useState("");
    const [selectedSubtype, setSelectedSubtype] = useState("");
    const [selectedColor, setSelectedColor] = useState("");
    const [selectedPriority, setSelectedPriority] = useState("");
    const [colors, setColors] = useState([]);
    const [editColor, setEditColor] = useState(null);
    const [availableSchemes, setAvailableSchemes] = useState([]);
    const [availableColors, setAvailableColors] = useState([]);
    const [selectedScheme, setSelectedScheme] = useState("");
    const [variantColors, setVariantColors] = useState([]);
    const [variantSearch, setVariantSearch] = useState("");
    const [showVariantTable, setShowVariantTable] = useState(false);
    const [currentTypeId, setCurrentTypeId] = useState(null);
    const [currentSubtypeId, setCurrentSubtypeId] = useState(null);
    const [editingResponse, setEditingResponse] = useState(null);
    const [editContent, setEditContent] = useState("");
    const [conditionsValue, setConditionsValue] = useState("");
    const [savingConditions, setSavingConditions] = useState(false);
    const [isEditingConditions, setIsEditingConditions] = useState(false);
    const [pdfPreview, setPdfPreview] = useState({ open: false, src: "" });
    const [imagePreview, setImagePreview] = useState({
      open: false,
      src: "",
      title: "",
    });

    const [files, setFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState(false);

    const [loadingTypes, setLoadingTypes] = useState(true);
    const [loadingSubtypes, setLoadingSubtypes] = useState(false);
    const [loadingPriorities, setLoadingPriorities] = useState(true);
    const [daysToComplete, setDaysToComplete] = useState("");
    const [daysNotes, setDaysNotes] = useState("");
    const [savingDays, setSavingDays] = useState(false);
    const [isEditingCompletionDays, setIsEditingCompletionDays] = useState(false);

    const [snackbar, setSnackbar] = useState({
      open: false,
      message: "",
      severity: "success",
    });

    const [contractor, setContractor] = useState(null);
    const editRequestRef = useRef();

    const permissions = useContext(PermissionsContext);
    const hasPermission = (perm) => permissions?.includes(perm);

    const [editContractorOpen, setEditContractorOpen] = useState(false);
    const [contractorForm, setContractorForm] = useState({
      contractor_name: "",
      phone_number: "",
      company_name: "",
      notes: "",
    });

    const pageCardSx = {
      borderRadius: "18px",
      border: "1px solid rgba(47,99,173,0.08)",
      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
      backgroundColor: "rgba(255,255,255,0.96)",
    };

    const sectionCardSx = {
      ...pageCardSx,
      p: 2,
      height: "100%",
    };

    const innerSoftSx = {
      borderRadius: "14px",
      border: "1px solid rgba(47,99,173,0.08)",
      backgroundColor: "rgba(47,99,173,0.03)",
    };

    const sectionHeaderIconSx = {
      width: 36,
      height: 36,
      minWidth: 36,
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(47,99,173,0.08)",
      flexShrink: 0,
    };

    const primaryButtonSx = {
      backgroundColor: "#2f63ad",
      color: "white",
      fontWeight: 700,
      borderRadius: "10px",
      textTransform: "none",
      px: 2.25,
      boxShadow: "none",
      "&:hover": {
        backgroundColor: "#1f4f94",
        boxShadow: "none",
      },
    };

    const secondaryButtonSx = {
      borderRadius: "10px",
      textTransform: "none",
      fontWeight: 600,
      px: 2,
      borderColor: "rgba(47,99,173,0.18)",
      color: "#223142",
      whiteSpace: "nowrap",
      "&:hover": {
        borderColor: "#2f63ad",
        color: "#2f63ad",
        backgroundColor: "rgba(47,99,173,0.04)",
      },
    };

    const textFieldSx = {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "rgba(255,255,255,0.96)",
        borderRadius: "10px",
      },
    };

    const dialogPaperSx = {
      borderRadius: "18px",
      background: "rgba(255,255,255,0.98)",
      boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
    };

    useImperativeHandle(ref, () => ({
      getPrintData: () => {
        const typeName =
          types.find((t) => t.id === selectedType)?.type ||
          requestDetails?.request_type_name ||
          requestDetails?.request_type?.[1] ||
          "N/A";

        const subtypeName =
          subtypes.find((s) => s.subtype_id === selectedSubtype)?.subtype_name ||
          requestDetails?.subtype_name ||
          "N/A";

        const normalizedAcknowledged =
          requestDetails?.acknowledged === 0 ||
          requestDetails?.acknowledged === "0" ||
          requestDetails?.acknowledged === false
            ? 0
            : 1;

        return {
          contractor,
          colors,
          member,
          files,
          filesLoading,
          requestDetails: {
            ...requestDetails,
            type_name: typeName,
            subtype_name: subtypeName,
            acknowledged: normalizedAcknowledged,
            freeform_responses: requestDetails?.freeform_responses || [],
          },
        };
      },
    }));

    useEffect(() => {
      const loadInitialData = async () => {
        try {
          const [typeRes, priorityRes, userRes] = await Promise.all([
            api.get(`/architectural-request-types`),
            api.get(`/priority-levels`),
            api.get(`/users-by-role`),
          ]);
          setTypes(typeRes.data || []);
          setPriorities(priorityRes.data || []);
          setUsers(userRes.data || []);
        } catch (err) {
          console.error("Failed to load initial data", err);
        } finally {
          setLoadingTypes(false);
          setLoadingPriorities(false);
        }
      };
      loadInitialData();
    }, []);

    // Load multi-type selections when request changes
    useEffect(() => {
      if (!requestDetails?.id) return;
      api.get(`/architectural-requests/${requestDetails.id}/multi-types`)
        .then(r => setMultiTypes(r.data || []))
        .catch(() => setMultiTypes([]));
    }, [requestDetails?.id]);

    useEffect(() => {
      const v = requestDetails?.completion_days_current?.days_to_complete;
      setDaysToComplete(v !== null && v !== undefined ? String(v) : "");
      setDaysNotes("");
      setIsEditingCompletionDays(false);
    }, [
      requestDetails?.id,
      requestDetails?.completion_days_current?.days_to_complete,
      requestDetails?.completion_days_current?.notes,
    ]);

    useEffect(() => {
      setConditionsValue(requestDetails?.conditions ?? "");
      setIsEditingConditions(false);
    }, [requestDetails?.id, requestDetails?.conditions]);

    useEffect(() => {
      const fetchLetterLogs = async () => {
        if (!requestDetails?.id) return;
        try {
          const res = await api.get(
            `/architectural-requests/${requestDetails.id}/letter-logs`
          );
          setLetterLogs(res.data || []);
        } catch (err) {
          console.error("Failed to load letter logs:", err);
        }
      };
      fetchLetterLogs();
    }, [requestDetails?.id]);

    useEffect(() => {
      const fetchRequestFiles = async () => {
        if (!requestDetails?.id) return;

        try {
          setFilesLoading(true);
          const res = await api.get(`/request-files/${requestDetails.id}`);
          setFiles(res.data || []);
          
        } catch (err) {
          console.error("Failed to load request files:", err);
          setFiles([]);
        } finally {
          setFilesLoading(false);
        }
      };

      fetchRequestFiles();
    }, [requestDetails?.id]);

    useEffect(() => {
      if (!requestDetails || types.length === 0) return;

      if (requestDetails.assigned_to) setSelectedUser(requestDetails.assigned_to);
      if (requestDetails.priority) setSelectedPriority(requestDetails.priority);
      if (requestDetails.color_id) setSelectedColor(requestDetails.color_id);

      let typeId = null;
      const { request_type } = requestDetails;

      if (Array.isArray(request_type)) typeId = Number(request_type[0]);
      else if (typeof request_type === "object" && request_type !== null)
        typeId = Number(request_type.id);
      else if (request_type) typeId = Number(request_type);

      if (typeId) setSelectedType(typeId);
      if (requestDetails.subtype_id)
        setSelectedSubtype(Number(requestDetails.subtype_id));
    }, [requestDetails, types]);

    useEffect(() => {
      if (!selectedType) return;
      const fetchSubtypes = async () => {
        setLoadingSubtypes(true);
        try {
          const res = await api.get(
            `/architectural-request-subtypes?typeId=${selectedType}`
          );
          setSubtypes(res.data || []);
        } catch (err) {
          console.error("Failed to load subtypes", err);
        } finally {
          setLoadingSubtypes(false);
        }
      };
      fetchSubtypes();
    }, [selectedType]);

    useEffect(() => {
      const fetchContractor = async () => {
        if (!requestDetails?.id) return;
        try {
          const res = await api.get(
            `/architectural-request-contractors/${requestDetails.id}`
          );
          if (res.data && Object.keys(res.data).length > 0) setContractor(res.data);
          else setContractor(null);
        } catch (err) {
          console.error("No contractor info found or failed to load:", err);
          setContractor(null);
        }
      };
      fetchContractor();
    }, [requestDetails && requestDetails.id]);

    useEffect(() => {
      const fetchColorSelections = async () => {
        if (!requestDetails?.id) return;
        try {
          const res = await api.get(`/architectural-requests/${requestDetails.id}`);
          if (res.data?.color_selections?.length > 0)
            setColors(res.data.color_selections);
          else setColors([]);
        } catch (err) {
          console.error("Failed to load color selection info:", err);
          setColors([]);
        }
      };
      fetchColorSelections();
    }, [requestDetails?.id]);

    const handleEditColor = async (color) => {
      if (!hasPermission("edit_architectural_request_color")) return;
      setEditColor(color);
      setCurrentTypeId(color.type_id);
      setCurrentSubtypeId(color.subtype_id);

      try {
        const schemeRes = await api.get(`/color-management`);
        setAvailableSchemes(schemeRes.data || []);
        setSelectedScheme(color.scheme_id);
        setSelectedColor(color.color_option_id);

        const colorRes = await api.get(`/color-options/by-scheme`, {
          params: {
            scheme_id: color.scheme_id,
            type_id: color.type_id,
            subtype_id: color.subtype_id,
          },
        });
        setAvailableColors(colorRes.data || []);
      } catch (err) {
        console.error("Failed to load color data:", err);
        setAvailableSchemes([]);
        setAvailableColors([]);
      }
    };

    const handleDownloadStoredPDF = async (log) => {
      try {
        if (!log.file_id) {
          setSnackbar({
            open: true,
            message: "This log entry does not have an attached file.",
            severity: "warning",
          });
          return;
        }

        const res = await api.get(`/architectural-request-files/file/${log.file_id}`);
        const file = res.data;

        if (!file?.file_url) {
          setSnackbar({
            open: true,
            message: "File URL missing in record.",
            severity: "error",
          });
          return;
        }

        const correctUrl = `${api.defaults.baseURL}/architecturalletters/${
          file.architectural_request_id
        }/${encodeURIComponent(file.file_url)}`;

        setPdfPreview({ open: true, src: correctUrl });
      } catch (err) {
        console.error("Failed to load stored architectural PDF:", err);
        setSnackbar({
          open: true,
          message: "Unable to load stored PDF.",
          severity: "error",
        });
      }
    };

    const handleSaveConditions = async () => {
      try {
        setSavingConditions(true);

        await api.put(`/architectural-requests/${requestDetails.id}`, {
          conditions: conditionsValue,
          user_id: currentUserId,
        });

        const refreshed = await api.get(`/architectural-requests/${requestDetails.id}`);
        onUpdate(refreshed.data);

        setSnackbar({
          open: true,
          message: "Conditions updated",
          severity: "success",
        });
      } catch (err) {
        console.error(err);
        setSnackbar({
          open: true,
          message: "Failed to update conditions",
          severity: "error",
        });
      } finally {
        setSavingConditions(false);
      }
    };

    const handleSaveCompletionDays = async () => {
      try {
        setSavingDays(true);

        const parsed = parseInt(daysToComplete, 10);
        if (Number.isNaN(parsed) || parsed < 0) {
          setSnackbar({
            open: true,
            message: "Days must be a non-negative number",
            severity: "warning",
          });
          return;
        }

        await api.put(`/architectural-requests/${requestDetails.id}`, {
          days_to_complete: parsed,
          completion_notes: daysNotes?.trim() || null,
          user_id: currentUserId,
        });

        const refreshed = await api.get(`/architectural-requests/${requestDetails.id}`);
        onUpdate(refreshed.data);

        setSnackbar({
          open: true,
          message: "Completion days updated",
          severity: "success",
        });
        setDaysNotes("");
      } catch (err) {
        console.error(err);
        setSnackbar({
          open: true,
          message: "Failed to update completion days",
          severity: "error",
        });
      } finally {
        setSavingDays(false);
      }
    };

    const getCompletionProgress = () => {
      const rec = requestDetails?.completion_days_current;
      if (!rec?.recorded_at) return null;

      const startDate = new Date(rec.recorded_at);
      const totalDays = Number(rec.days_to_complete ?? 0);
      if (!totalDays) return null;

      const now = new Date();
      const msPerDay = 1000 * 60 * 60 * 24;

      const daysPassed = (now - startDate) / msPerDay;
      const daysRemaining = totalDays - daysPassed;
      const percent = Math.min(100, Math.max(0, (daysPassed / totalDays) * 100));

      return { daysPassed, daysRemaining, overdue: daysRemaining < 0, percent };
    };

    useEffect(() => {
      const interval = setInterval(() => {
        setSavingDays((v) => v);
      }, 60000);
      return () => clearInterval(interval);
    }, []);

    const handleUpdate = async (field, value) => {
      try {
        let payload = {};
        let updatedRequest = { ...requestDetails };

        switch (field) {
          case "request_type_id": {
            const selected = types.find((t) => t.id === Number(value));
            payload = { request_type: Number(value) };
            updatedRequest.request_type = [selected?.id.toString(), selected?.type];
            updatedRequest.subtype_id = null;
            setSelectedType(Number(value));
            setSelectedSubtype("");
            break;
          }

          case "subtype_id":
            payload = { subtype_id: Number(value) };
            updatedRequest.subtype_id = Number(value);
            setSelectedSubtype(Number(value));
            break;

          case "color_id":
            payload = { color_id: Number(value) };
            updatedRequest.color_id = Number(value);
            setSelectedColor(Number(value));
            break;

          case "priority_value": {
            payload = { priority: value };
            const priorityObj = priorities.find((p) => p.priority_value === value);
            updatedRequest.priority = value;
            updatedRequest.priority_color = priorityObj?.color_code || "#1976d2";
            setSelectedPriority(value);
            break;
          }

          case "anticipated_start_date":
          case "anticipated_completion_date":
            payload = { [field]: value };
            updatedRequest[field] = value;
            break;

          case "assigned_to": {
            try {
              await api.put(`/assignArchitecturalRequest`, {
                request_id: requestDetails.id,
                assign_to: value,
              });
              setSelectedUser(value);
              onUpdate({ ...requestDetails, assigned_to: value });
              setSnackbar({
                open: true,
                message: "Assigned and email sent",
                severity: "success",
              });
            } catch (err) {
              console.error("Failed to assign and send email:", err);
              setSnackbar({
                open: true,
                message: "Assigned, but email failed to send",
                severity: "warning",
              });
            }
            return;
          }

          default:
            return;
        }

        await api.put(`/architectural-requests/${requestDetails.id}`, {
          ...payload,
          user_id: currentUserId,
        });

        onUpdate({ ...requestDetails, ...updatedRequest });

        setSnackbar({
          open: true,
          message: "Request updated successfully",
          severity: "success",
        });
      } catch (err) {
        console.error(`Failed to update ${field}`, err);
        setSnackbar({
          open: true,
          message: "Failed to update request",
          severity: "error",
        });
      }
    };

    const SectionTitle = ({ icon, title, action = null }) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Box sx={sectionHeaderIconSx}>{icon}</Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.2 }}
          >
            {title}
          </Typography>
        </Box>
        {action}
      </Box>
    );

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Card sx={{ ...sectionCardSx, display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            onClick={() => editRequestRef.current?.openModal()}
            startIcon={<PendingActionsIcon />}
            sx={primaryButtonSx}
          >
            Change Status
          </Button>
        </Card>

        <Box sx={{ textAlign: "center" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#6b778c" }}>
            Request ID: <strong>REQ-{requestDetails?.id}</strong>
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            {requestDetails?.acknowledged !== null ? (
              <Box
                sx={{
                  ...innerSoftSx,
                  p: 2,
                  backgroundColor: "#e8f5e9",
                  borderColor: "#c8e6c9",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  justifyContent: "center",
                }}
              >
                <CheckCircleIcon color="success" />
                <Typography variant="body1" sx={{ color: "#2e7d32", fontWeight: 500 }}>
                  Member has acknowledged the architectural request terms.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  ...innerSoftSx,
                  p: 2,
                  backgroundColor: "#fff3e0",
                  borderColor: "#ffe0b2",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  justifyContent: "center",
                }}
              >
                <InfoIcon color="warning" />
                <Typography variant="body1" sx={{ color: "#ef6c00", fontWeight: 500 }}>
                  Member has not acknowledged the terms yet.
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid container rowSpacing={6} columnSpacing={3} sx={{ alignItems: "stretch", mb: 1 }}>
            {requestDetails?.arc_category_id ? (
              /* ── Custom form: show category breadcrumb + optional batch panel ── */
              <React.Fragment>
                <Grid item xs={12}>
                  <Card sx={{ ...sectionCardSx, p: 2.5 }}>
                    <SectionTitle
                      icon={<CategoryIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                      title="Category"
                    />
                    <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                      {(requestDetails.arc_category_breadcrumb || "").split(" › ").map((part, i, arr) => (
                        <React.Fragment key={i}>
                          <Typography variant="body2" sx={{ fontWeight: i === arr.length - 1 ? 700 : 400, color: i === arr.length - 1 ? "#223142" : "#6b778c" }}>
                            {part}
                          </Typography>
                          {i < arr.length - 1 && (
                            <Typography variant="body2" sx={{ color: "#b0bec5", mx: 0.25 }}>›</Typography>
                          )}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Card>
                </Grid>

                {requestDetails?.batch_id && requestDetails?.batch_siblings?.length > 0 && (
                  <Grid item xs={12}>
                    <BatchPanel
                      batchId={requestDetails.batch_id}
                      currentId={requestDetails.id}
                      siblings={requestDetails.batch_siblings}
                      onBatchStatusChange={onUpdate}
                    />
                  </Grid>
                )}
              </React.Fragment>
            ) : (
              /* ── Standard form: show Request Type + Subtype ── */
              <React.Fragment>
                <Grid item xs={12} md={6}>
                  <Card sx={{ ...sectionCardSx, p: 2.5 }}>
                    <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:1 }}>
                      <SectionTitle
                        icon={<InfoIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                        title={multiTypes.length > 1 ? `Request Types (${multiTypes.length})` : "Request Type"}
                      />
                      {multiTypes.length > 1 && hasPermission("edit_architectural_request_type") && (
                        <Button size="small" variant="outlined" onClick={() => {
                          setEditMultiEntries(multiTypes.map(m => ({ type_id: String(m.type_id), subtype_id: String(m.subtype_id || ""), type_name: m.type_name, subtype_name: m.subtype_name })));
                          const subMap = {};
                          multiTypes.forEach(async (m, i) => {
                            if (m.type_id) {
                              try { const r = await api.get(`/architectural-request-subtypes?typeId=${m.type_id}`); subMap[i] = r.data || []; } catch {}
                              setEditMultiSubtypes(prev => ({ ...prev, ...subMap }));
                            }
                          });
                          setEditMultiOpen(true);
                        }} sx={{ borderRadius:"8px", textTransform:"none", fontSize:12, fontWeight:600 }}>
                          Edit Types
                        </Button>
                      )}
                    </Box>
                    {multiTypes.length > 1 ? (
                      <Box>
                        {multiTypes.map((mt, i) => (
                          <Box key={mt.id} sx={{ mb: i < multiTypes.length - 1 ? 1 : 0,
                            p: 1.25, borderRadius: "8px", bgcolor: "rgba(47,99,173,0.04)",
                            border: "1px solid rgba(47,99,173,0.1)" }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>{mt.type_name || "Unknown Type"}</Typography>
                            <Typography variant="caption" sx={{ color: "#6b778c" }}>{mt.subtype_name ? `Subtype: ${mt.subtype_name}` : "No subtype"}</Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : hasPermission("edit_architectural_request_type") ? (
                      <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                        <InputLabel>Request Type</InputLabel>
                        <Select label="Request Type" value={selectedType || ""}
                          onChange={(e) => handleUpdate("request_type_id", e.target.value)} disabled={loadingTypes}>
                          {types.map((type) => <MenuItem key={type.id} value={type.id}>{type.type}</MenuItem>)}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="body2">{types.find((t) => t.id === selectedType)?.type || "N/A"}</Typography>
                    )}
                  </Card>
                </Grid>

                {colors.length === 0 && multiTypes.length <= 1 && (
                  <Grid item xs={12} md={6}>
                    <Card sx={{ ...sectionCardSx, p: 2.5 }}>
                      <SectionTitle
                        icon={<CategoryIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                        title="Subtype"
                      />
                      {hasPermission("edit_architectural_request_subtype") ? (
                        <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                          <InputLabel>Subtype</InputLabel>
                          <Select label="Subtype" value={selectedSubtype || ""}
                            onChange={(e) => handleUpdate("subtype_id", e.target.value)}
                            disabled={!selectedType || loadingSubtypes}>
                            {subtypes.map((sub) => (
                              <MenuItem key={sub.subtype_id} value={sub.subtype_id}>{sub.subtype_name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body2">
                          {subtypes.find((s) => s.subtype_id === selectedSubtype)?.subtype_name || "N/A"}
                        </Typography>
                      )}
                    </Card>
                  </Grid>
                )}
              </React.Fragment>
            )}

            {colors.length > 0 && (
              <Grid item xs={12}>
                <Card sx={{ ...sectionCardSx, p: 2.5 }}>
                  <SectionTitle
                    icon={<InfoIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                    title="Color Selection Details"
                  />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {colors.map((color) => (
                      <Box key={color.id} sx={{ ...innerSoftSx, p: 1.5 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                          gap={1}
                          flexWrap="wrap"
                        >
                          <Typography variant="subtitle1" fontWeight={600}>
                            {color.scheme_name || "Unnamed Scheme"}
                          </Typography>

                          {hasPermission("edit_architectural_request_color") && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleEditColor(color)}
                              sx={secondaryButtonSx}
                            >
                              Edit
                            </Button>
                          )}
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Typography variant="body2">
                            <strong>Subtype:</strong> {color.subtype_name || "N/A"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Color Name:</strong>{" "}
                            {color.option_color_name || color.scheme_name || "N/A"}
                          </Typography>
                        </Box>

                        {color.scheme_hex && (
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "6px",
                              border: "1px solid #aaa",
                              backgroundColor: color.scheme_hex,
                              mt: 1,
                            }}
                          />
                        )}

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.75, display: "block" }}
                        >
                          <strong>Created:</strong>{" "}
                          {color.created_at
                            ? new Date(color.created_at).toLocaleString()
                            : "N/A"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Card sx={{ ...sectionCardSx, p: 2.5 }}>
                <SectionTitle
                  icon={<PendingActionsIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                  title="Priority"
                />
                <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    label="Priority"
                    value={selectedPriority || ""}
                    onChange={(e) => handleUpdate("priority_value", e.target.value)}
                    disabled={loadingPriorities}
                  >
                    {priorities.map((priority) => (
                      <MenuItem
                        key={priority.priority_value}
                        value={priority.priority_value}
                      >
                        {priority.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ ...sectionCardSx, p: 2.5 }}>
                <SectionTitle
                  icon={<CheckCircleIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                  title="Assigned To"
                />
                {hasPermission("edit_architectural_request_assignee") ? (
                  <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                    <InputLabel>Assign To</InputLabel>
                    <Select
                      label="Assign To"
                      value={selectedUser || ""}
                      onChange={(e) => handleUpdate("assigned_to", e.target.value)}
                    >
                      {users.map((user) => (
                        <MenuItem key={user.UserID} value={user.UserID}>
                          {user.Name} {user.Lastname} ({user.RoleName})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="body2">
                    {users.find((u) => String(u.UserID) === String(selectedUser))
                      ? `${
                          users.find((u) => String(u.UserID) === String(selectedUser)).Name
                        } ${
                          users.find((u) => String(u.UserID) === String(selectedUser))
                            .Lastname
                        }`
                      : "N/A"}
                  </Typography>
                )}
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ ...sectionCardSx, p: 2.5 }}>
                <SectionTitle
                  icon={<DescriptionIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                  title="Description"
                />
                <Typography variant="body2">
                  {requestDetails?.description || "No Description Available"}
                </Typography>
              </Card>
            </Grid>

            {requestDetails?.freeform_responses?.length > 0 && (
              <Grid item xs={12}>
                <Card sx={{ ...sectionCardSx, p: 2.5, backgroundColor: "#fafafa" }}>
                  <SectionTitle
                    icon={<InfoIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                    title="Freeform Response"
                  />

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {requestDetails.freeform_responses.map((response) => (
                      <Box
                        key={response.id}
                        sx={{
                          ...innerSoftSx,
                          p: 1.5,
                          backgroundColor: "#fff",
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                          gap={1}
                          flexWrap="wrap"
                        >
                          <Typography variant="subtitle1" fontWeight={600}>
                            {response.subtype_name || "N/A"}
                          </Typography>

                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setEditingResponse(response);
                              setEditContent(response.field_content || "");
                            }}
                            sx={secondaryButtonSx}
                          >
                            Edit
                          </Button>
                        </Box>

                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Type:</strong> {response.type_name || "N/A"}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                          <strong>Response:</strong> {response.field_content || "N/A"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          Added on{" "}
                          {response.created_at
                            ? new Date(response.created_at).toLocaleString()
                            : "N/A"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            )}

            <Grid item xs={12}>
              <Card sx={{ ...sectionCardSx, p: 2.5 }}>
                <SectionTitle
                  icon={<PendingActionsIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                  title="Anticipated Dates"
                />

                <Box display="flex" flexDirection="column" gap={2}>
                  {hasPermission("edit_architectural_request_dates") ? (
                    <>
                      <TextField
                        label="Anticipated Start Date"
                        type="date"
                        size="small"
                        fullWidth
                        value={
                          requestDetails?.anticipated_start_date
                            ? requestDetails.anticipated_start_date.split("T")[0]
                            : ""
                        }
                        onChange={(e) =>
                          handleUpdate("anticipated_start_date", e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={textFieldSx}
                      />

                      <TextField
                        label="Anticipated Completion Date"
                        type="date"
                        size="small"
                        fullWidth
                        value={
                          requestDetails?.anticipated_completion_date
                            ? requestDetails.anticipated_completion_date.split("T")[0]
                            : ""
                        }
                        onChange={(e) =>
                          handleUpdate("anticipated_completion_date", e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={textFieldSx}
                      />
                    </>
                  ) : (
                    <>
                      <Typography variant="body2">
                        <strong>Anticipated Start Date:</strong>{" "}
                        {requestDetails?.anticipated_start_date
                          ? new Date(
                              requestDetails.anticipated_start_date
                            ).toLocaleDateString()
                          : "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Anticipated Completion Date:</strong>{" "}
                        {requestDetails?.anticipated_completion_date
                          ? new Date(
                              requestDetails.anticipated_completion_date
                            ).toLocaleDateString()
                          : "N/A"}
                      </Typography>
                    </>
                  )}
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ ...sectionCardSx, p: 2.5 }}>
                <SectionTitle
                  icon={
                    <CheckCircleIcon
                      sx={{
                        color:
                          requestDetails?.status === "Approved"
                            ? "#2e7d32"
                            : "#d32f2f",
                        fontSize: 18,
                      }}
                    />
                  }
                  title="Status"
                />
                <Typography variant="body2">{requestDetails?.status || "N/A"}</Typography>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ ...sectionCardSx, p: 2.5 }}>
                <SectionTitle
                  icon={<PendingActionsIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                  title="Completion Days"
                  action={
                    !isEditingCompletionDays &&
                    hasPermission("edit_architectural_request_dates") ? (
                      <Tooltip title="Edit Completion Days">
                        <IconButton
                          size="small"
                          onClick={() => setIsEditingCompletionDays(true)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null
                  }
                />

                {!isEditingCompletionDays ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {requestDetails?.completion_days_current?.days_to_complete ?? "N/A"}{" "}
                      days
                    </Typography>

                    {requestDetails?.completion_days_current?.recorded_at && (
                      <Typography variant="caption" color="text.secondary">
                        Set{" "}
                        {new Date(
                          requestDetails.completion_days_current.recorded_at
                        ).toLocaleString()}
                      </Typography>
                    )}

                    {requestDetails?.completion_days_current?.notes && (
                      <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                        <strong>Notes:</strong>{" "}
                        {requestDetails.completion_days_current.notes}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Days to Complete"
                      value={daysToComplete}
                      onChange={(e) => setDaysToComplete(e.target.value)}
                      inputProps={{ min: 0 }}
                      sx={{ width: "180px", ...textFieldSx }}
                    />

                    <TextField
                      size="small"
                      label="Notes (optional)"
                      value={daysNotes}
                      onChange={(e) => setDaysNotes(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      sx={textFieldSx}
                    />

                    <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveCompletionDays}
                        disabled={savingDays}
                        sx={primaryButtonSx}
                      >
                        {savingDays ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          "Save"
                        )}
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CloseIcon />}
                        onClick={() => {
                          const v =
                            requestDetails?.completion_days_current?.days_to_complete;
                          setDaysToComplete(
                            v !== null && v !== undefined ? String(v) : ""
                          );
                          setDaysNotes("");
                          setIsEditingCompletionDays(false);
                        }}
                        sx={secondaryButtonSx}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}

                {(() => {
                  const progress = getCompletionProgress();
                  if (!progress) return null;

                  return (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progress.percent}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: "#eee",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: progress.overdue ? "#d32f2f" : "#2e7d32",
                          },
                        }}
                      />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {progress.overdue ? (
                          <span style={{ color: "#d32f2f" }}>
                            Overdue by {Math.abs(progress.daysRemaining).toFixed(1)} day
                            {Math.abs(progress.daysRemaining) >= 1.5 && "s"}
                          </span>
                        ) : (
                          <span style={{ color: "#2e7d32" }}>
                            {progress.daysRemaining.toFixed(1)} day
                            {progress.daysRemaining >= 1.5 && "s"} remaining
                          </span>
                        )}
                      </Typography>
                    </Box>
                  );
                })()}
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ ...sectionCardSx, p: 2.5 }}>
                <SectionTitle
                  icon={<InfoIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                  title="Conditions"
                  action={
                    hasPermission("edit_architectural_request_dates") &&
                    !isEditingConditions ? (
                      <Tooltip title="Edit Conditions">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setConditionsValue(requestDetails?.conditions ?? "");
                            setIsEditingConditions(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null
                  }
                />

                {!isEditingConditions ? (
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-line", color: "text.primary" }}
                  >
                    {requestDetails?.conditions &&
                    String(requestDetails.conditions).trim().length > 0
                      ? requestDetails.conditions
                      : "N/A"}
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      size="small"
                      label="Conditions"
                      value={conditionsValue}
                      onChange={(e) => setConditionsValue(e.target.value)}
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Enter any approval conditions (optional)"
                      sx={textFieldSx}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SaveIcon />}
                        disabled={savingConditions}
                        onClick={async () => {
                          await handleSaveConditions();
                          setIsEditingConditions(false);
                        }}
                        sx={primaryButtonSx}
                      >
                        {savingConditions ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          "Save"
                        )}
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CloseIcon />}
                        onClick={() => {
                          setConditionsValue(requestDetails?.conditions ?? "");
                          setIsEditingConditions(false);
                        }}
                        sx={secondaryButtonSx}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}
              </Card>
            </Grid>

            {contractor ? (
              <Grid item xs={12}>
                <Card sx={{ ...sectionCardSx, p: 2.5 }}>
                  <SectionTitle
                    icon={<BusinessIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                    title="Contractor Information"
                    action={
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setContractorForm(contractor);
                          setEditContractorOpen(true);
                        }}
                        sx={secondaryButtonSx}
                      >
                        Edit
                      </Button>
                    }
                  />

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon color="info" />
                      <Typography>
                        <strong>Name:</strong> {contractor.contractor_name}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneIcon color="success" />
                      <Typography>
                        <strong>Phone:</strong> {contractor.phone_number || "N/A"}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      <BusinessIcon color="secondary" />
                      <Typography>
                        <strong>Company:</strong> {contractor.company_name || "N/A"}
                      </Typography>
                    </Box>

                    {contractor.notes && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>Notes:</strong> {contractor.notes}
                      </Typography>
                    )}
                  </Box>
                </Card>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Card sx={{ ...sectionCardSx, p: 2.5, textAlign: "center" }}>
                  <SectionTitle
                    icon={<BusinessIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                    title="Contractor Information"
                  />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    No contractor information added
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setContractorForm({
                        contractor_name: "",
                        phone_number: "",
                        company_name: "",
                        notes: "",
                      });
                      setEditContractorOpen(true);
                    }}
                    sx={primaryButtonSx}
                  >
                    Add Contractor
                  </Button>
                </Card>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <RequestFilesViewer
              requestId={requestDetails?.id}
              onPreviewImage={(src, title = "") => {
                setImagePreview({
                  open: true,
                  src,
                  title,
                });
              }}
            />
          </Grid>
        </Grid>

        <Card sx={sectionCardSx}>
          <SectionTitle
            icon={<PdfIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
            title="Letter History"
          />

          {letterLogs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No letters have been sent or exported yet.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {letterLogs.map((log) => (
                <Accordion
                  key={log.id}
                  sx={{
                    borderRadius: "12px !important",
                    boxShadow: "none",
                    border: "1px solid rgba(47,99,173,0.08)",
                    overflow: "hidden",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ flexGrow: 1 }}>
                      {log.action === "email" ? <EmailIcon /> : <PdfIcon />}{" "}
                      <strong>{log.template_name || "Architectural Letter"}</strong> -{" "}
                      {new Date(log.created_at).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sent by {log.sent_by_firstname} {log.sent_by_lastname}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Recipient:</strong> {log.recipient || "N/A"} <br />
                      <strong>Address Type:</strong> {log.address_type || "N/A"}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        padding: 2,
                        backgroundColor: "#fafafa",
                        maxHeight: "300px",
                        overflowY: "auto",
                      }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(log.template_body || '') }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PdfIcon />}
                      onClick={() => handleDownloadStoredPDF(log)}
                      sx={{ textTransform: "none", mt: 1, ...secondaryButtonSx }}
                    >
                      Regenerate PDF
                    </Button>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Card>

        <Dialog
          open={Boolean(imagePreview.open)}
          onClose={() => setImagePreview({ open: false, src: "", title: "" })}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogContent
            sx={{
              position: "relative",
              backgroundColor: "#111",
              p: 2,
              textAlign: "center",
            }}
          >
            <IconButton
              onClick={() => setImagePreview({ open: false, src: "", title: "" })}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 1,
                bgcolor: "rgba(0,0,0,0.45)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>

            <Box
              component="img"
              src={imagePreview.src}
              alt={imagePreview.title || "Preview"}
              sx={{
                maxWidth: "100%",
                maxHeight: "85vh",
                objectFit: "contain",
                borderRadius: "10px",
              }}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={!!editColor}
          onClose={() => setEditColor(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: "#223142" }}>
            Edit Color Selection
          </DialogTitle>

          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {editColor && (
              <Box sx={{ ...innerSoftSx, mb: 1, p: 1.5 }}>
                <Typography variant="body2">
                  <strong>Current Scheme:</strong> {editColor.scheme_name || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Color:</strong> {editColor.option_color_name || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>Subtype:</strong> {editColor.subtype_name || "N/A"}
                </Typography>
              </Box>
            )}

            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel>Scheme</InputLabel>
              <Select
                value={selectedScheme || ""}
                onChange={async (e) => {
                  const schemeId = e.target.value;
                  setSelectedScheme(schemeId);
                  setSelectedColor("");
                  setVariantColors([]);
                  setShowVariantTable(false);

                  if (!schemeId || !currentTypeId || !currentSubtypeId) return;

                  try {
                    const colorRes = await api.get(`/color-options/by-scheme`, {
                      params: {
                        scheme_id: Number(schemeId),
                        type_id: Number(currentTypeId),
                        subtype_id: Number(currentSubtypeId),
                      },
                    });

                    let colorsList = colorRes.data || [];

                    if (Number(currentSubtypeId) === 1061) {
                      colorsList.push({
                        id: "variant",
                        color_name: "Variant (All Colors)",
                      });
                    }

                    setAvailableColors(colorsList);
                  } catch (err) {
                    console.error("Failed to load color options:", err);
                    setAvailableColors([]);
                  }
                }}
              >
                {availableSchemes.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "4px",
                          backgroundColor: s.hex_value || "#ccc",
                          border: "1px solid #aaa",
                        }}
                      />
                      <Typography>{s.color_name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!selectedScheme}>
              <InputLabel>Color</InputLabel>
              <Select
                value={selectedColor || ""}
                onChange={async (e) => {
                  const value = e.target.value;
                  setSelectedColor(value);

                  if (editColor.subtype_id === 1061 && value === "variant") {
                    try {
                      const allColorRes = await api.get(`/color-options`);
                      setVariantColors(allColorRes.data || []);
                      setShowVariantTable(true);
                    } catch (err) {
                      console.error("Failed to load variant colors:", err);
                      setVariantColors([]);
                      setShowVariantTable(false);
                    }
                  } else {
                    setShowVariantTable(false);
                  }
                }}
                renderValue={(selected) => {
                  if (!selected) return "Select Color";
                  if (selected === "variant") return "Variant (All Colors)";
                  const color = availableColors.find((c) => c.id === selected);
                  return color ? color.color_name : "Select Color";
                }}
              >
                {availableColors.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "4px",
                          backgroundColor: c.hex_value || "#ccc",
                          border: "1px solid #aaa",
                        }}
                      />
                      <Typography>{c.color_name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {showVariantTable && variantColors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Select a Variant Color from All Schemes:
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search colors..."
                  value={variantSearch}
                  onChange={(e) => setVariantSearch(e.target.value)}
                  sx={textFieldSx}
                />
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    mt: 2,
                    borderCollapse: "collapse",
                    backgroundColor: "#fafafa",
                    "& th, & td": {
                      border: "1px solid #ddd",
                      padding: "8px 12px",
                      textAlign: "left",
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th>Color Name</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variantColors
                      .filter((v) =>
                        v.color_name.toLowerCase().includes(variantSearch.toLowerCase())
                      )
                      .map((color) => (
                        <tr
                          key={color.id}
                          onClick={() => {
                            setSelectedColor(color.id);
                            setShowVariantTable(false);
                          }}
                          style={{
                            backgroundColor:
                              selectedColor === color.id ? "#e8f4ff" : "transparent",
                            cursor: "pointer",
                          }}
                        >
                          <td>{color.color_name}</td>
                          <td>{color.description || "-"}</td>
                        </tr>
                      ))}
                  </tbody>
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2.25, gap: 1 }}>
            <Button onClick={() => setEditColor(null)} variant="outlined" sx={secondaryButtonSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (!selectedScheme || !selectedColor) {
                  setSnackbar({
                    open: true,
                    message: "Please select both a scheme and color.",
                    severity: "warning",
                  });
                  return;
                }

                try {
                  await api.put(`/architectural-request-color-selections/${editColor.id}`, {
                    scheme_id: selectedScheme,
                    color_option_id: selectedColor,
                  });

                  setSnackbar({
                    open: true,
                    message: "Color selection updated successfully!",
                    severity: "success",
                  });

                  setEditColor(null);

                  const res = await api.get(`/architectural-requests/${requestDetails.id}`);
                  setColors(res.data.color_selections || []);
                } catch (err) {
                  console.error("Failed to update color selection:", err);
                  setSnackbar({
                    open: true,
                    message: "Failed to update color selection.",
                    severity: "error",
                  });
                }
              }}
              sx={primaryButtonSx}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(pdfPreview.open)}
          onClose={() => setPdfPreview({ open: false, src: "" })}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogContent
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "black",
              p: 2,
            }}
          >
            <IconButton
              onClick={() => setPdfPreview({ open: false, src: "" })}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                bgcolor: "rgba(0,0,0,0.4)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>

            <Box sx={{ width: "100%", height: "90vh", maxWidth: "1200px" }}>
              <object
                data={pdfPreview.src}
                type="application/pdf"
                width="100%"
                height="100%"
                style={{ border: "none" }}
              >
                <Typography color="white">
                  PDF preview not supported.{" "}
                  <Link
                    href={pdfPreview.src}
                    target="_blank"
                    rel="noopener"
                    sx={{ color: "#90caf9" }}
                  >
                    Open PDF
                  </Link>
                </Typography>
              </object>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                href={pdfPreview.src}
                target="_blank"
                download
                rel="noopener"
                sx={primaryButtonSx}
              >
                Download PDF
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        <Dialog
          open={editContractorOpen}
          onClose={() => setEditContractorOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Edit Contractor Information</DialogTitle>

          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Contractor Name"
              value={contractorForm.contractor_name}
              onChange={(e) =>
                setContractorForm({
                  ...contractorForm,
                  contractor_name: e.target.value,
                })
              }
              fullWidth
              sx={textFieldSx}
            />

            <TextField
              label="Phone Number"
              value={contractorForm.phone_number}
              onChange={(e) =>
                setContractorForm({
                  ...contractorForm,
                  phone_number: e.target.value,
                })
              }
              fullWidth
              sx={textFieldSx}
            />

            <TextField
              label="Company Name"
              value={contractorForm.company_name}
              onChange={(e) =>
                setContractorForm({
                  ...contractorForm,
                  company_name: e.target.value,
                })
              }
              fullWidth
              sx={textFieldSx}
            />

            <TextField
              label="Notes"
              value={contractorForm.notes}
              onChange={(e) =>
                setContractorForm({ ...contractorForm, notes: e.target.value })
              }
              fullWidth
              multiline
              minRows={3}
              sx={textFieldSx}
            />
          </DialogContent>

          <DialogActions sx={{ p: 2.25, gap: 1 }}>
            <Button onClick={() => setEditContractorOpen(false)} variant="outlined" sx={secondaryButtonSx}>
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await api.put(
                    `/architectural-request-contractors/${requestDetails.id}`,
                    contractorForm
                  );

                  const updated = await api.get(
                    `/architectural-request-contractors/${requestDetails.id}`
                  );
                  setContractor(updated.data);

                  setSnackbar({
                    open: true,
                    message: "Contractor information updated successfully",
                    severity: "success",
                  });

                  setEditContractorOpen(false);
                } catch (err) {
                  console.error(err);
                  setSnackbar({
                    open: true,
                    message: "Failed to update contractor information",
                    severity: "error",
                  });
                }
              }}
              sx={primaryButtonSx}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!editingResponse}
          onClose={() => setEditingResponse(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: dialogPaperSx }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Edit Freeform Response</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Typography variant="body2">
              <strong>Subtype:</strong> {editingResponse?.subtype_name}
            </Typography>
            <Typography variant="body2">
              <strong>Type:</strong> {editingResponse?.type_name}
            </Typography>

            <TextField
              multiline
              minRows={4}
              fullWidth
              label="Response"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              sx={textFieldSx}
            />
          </DialogContent>

          <DialogActions sx={{ p: 2.25, gap: 1 }}>
            <Button onClick={() => setEditingResponse(null)} variant="outlined" sx={secondaryButtonSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await api.put(
                    `/architectural-request-freeform-responses/${editingResponse.id}`,
                    {
                      field_content: editContent,
                    }
                  );

                  setSnackbar({
                    open: true,
                    message: "Response updated successfully!",
                    severity: "success",
                  });

                  const res = await api.get(`/architectural-requests/${requestDetails.id}`);
                  onUpdate(res.data);
                  setEditingResponse(null);
                } catch (err) {
                  console.error("Failed to update freeform response:", err);
                  setSnackbar({
                    open: true,
                    message: "Failed to update response.",
                    severity: "error",
                  });
                }
              }}
              sx={primaryButtonSx}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Edit Multi-Type Dialog ─────────────────────────────────────── */}
        <Dialog open={editMultiOpen} onClose={() => setEditMultiOpen(false)} maxWidth="sm" fullWidth
          PaperProps={{ sx:{ borderRadius:"16px" } }}>
          <DialogTitle sx={{ fontWeight:700, color:"#223142" }}>Edit Request Types</DialogTitle>
          <DialogContent>
            {editMultiEntries.map((entry, idx) => (
              <Box key={idx} sx={{ mb:1.5, p:1.5, borderRadius:"10px", bgcolor:"rgba(47,99,173,0.04)", border:"1px solid rgba(47,99,173,0.1)" }}>
                <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:1 }}>
                  <Typography variant="caption" sx={{ fontWeight:700, color:"#6b778c" }}>Type {idx+1}</Typography>
                  {editMultiEntries.length > 1 && (
                    <Button size="small" color="error" onClick={() => setEditMultiEntries(prev => prev.filter((_,i)=>i!==idx))}
                      sx={{ minWidth:0, textTransform:"none", fontSize:11 }}>Remove</Button>
                  )}
                </Box>
                <FormControl fullWidth size="small" sx={{ mb:1 }}>
                  <InputLabel>Request Type</InputLabel>
                  <Select value={entry.type_id || ""} label="Request Type"
                    onChange={async (e) => {
                      const tid = e.target.value;
                      setEditMultiEntries(prev => prev.map((en,i) => i===idx ? {...en,type_id:tid,subtype_id:""} : en));
                      if (tid) {
                        try {
                          const r = await api.get(`/architectural-request-subtypes?typeId=${tid}`);
                          setEditMultiSubtypes(prev => ({ ...prev, [idx]: r.data || [] }));
                        } catch {}
                      }
                    }}>
                    {types.map(t => <MenuItem key={t.id} value={String(t.id)}>{t.type}</MenuItem>)}
                  </Select>
                </FormControl>
                {(editMultiSubtypes[idx]?.length > 0) && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Subtype</InputLabel>
                    <Select value={entry.subtype_id || ""} label="Subtype"
                      onChange={e => setEditMultiEntries(prev => prev.map((en,i) => i===idx ? {...en,subtype_id:e.target.value} : en))}>
                      <MenuItem value="">None</MenuItem>
                      {(editMultiSubtypes[idx] || []).map(s => <MenuItem key={s.subtype_id} value={String(s.subtype_id)}>{s.subtype_name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
              </Box>
            ))}
            <Button size="small" onClick={() => setEditMultiEntries(prev => [...prev, { type_id:"", subtype_id:"" }])}
              sx={{ mt:0.5, borderRadius:"8px", textTransform:"none", color:"#2f63ad", border:"1px dashed rgba(47,99,173,0.3)", px:1.5 }}>
              + Add Another Type
            </Button>
          </DialogContent>
          <DialogActions sx={{ px:3, pb:3, gap:1 }}>
            <Button onClick={() => setEditMultiOpen(false)} variant="outlined"
              sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
            <Button variant="contained" disabled={savingMulti}
              sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad","&:hover":{bgcolor:"#1f4f94"} }}
              onClick={async () => {
                const valid = editMultiEntries.filter(e => e.type_id);
                if (!valid.length) return;
                setSavingMulti(true);
                try {
                  await api.post(`/architectural-requests/${requestDetails.id}/multi-types`, {
                    types: valid.map(e => ({ type_id: parseInt(e.type_id,10), subtype_id: e.subtype_id ? parseInt(e.subtype_id,10) : null }))
                  });
                  // Also update primary type
                  if (valid[0]?.type_id) await api.put(`/architectural-requests/${requestDetails.id}`, { request_type_id: parseInt(valid[0].type_id,10), subtype_id: valid[0].subtype_id ? parseInt(valid[0].subtype_id,10) : null }).catch(()=>{});
                  const r = await api.get(`/architectural-requests/${requestDetails.id}/multi-types`);
                  setMultiTypes(r.data || []);
                  setEditMultiOpen(false);
                  if (onUpdate) onUpdate();
                } catch (err) {
                  console.error('Save multi-types error:', err);
                } finally { setSavingMulti(false); }
              }}>
              {savingMulti ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

        <EditArchitecturalRequest
          ref={editRequestRef}
          architecturalRequestDetails={requestDetails}
          onUpdate={onUpdate}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            sx={{ borderRadius: 3 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
);

export default RequestDetailsPanel;